'use client';

import { useTranslation } from 'react-i18next';

import type { TenantInfo } from './tenant-privacy-policy';

interface TenantTermsOfServiceProps {
  tenant: TenantInfo;
  businessType?: string | null;
  cancellationWindowHours?: number;
  cancellationWindowHoursTrainer?: number | null;
}

interface TermsContentProps {
  tenant: TenantInfo;
  businessType?: string | null;
  cancellationWindowHours: number;
  cancellationWindowHoursTrainer: number | null;
}

export function TenantTermsOfService({
  tenant,
  businessType,
  cancellationWindowHours = 24,
  cancellationWindowHoursTrainer = null,
}: TenantTermsOfServiceProps) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  const contentProps: TermsContentProps = {
    tenant,
    businessType,
    cancellationWindowHours,
    cancellationWindowHoursTrainer,
  };

  return isArabic ? (
    <ArabicTerms {...contentProps} />
  ) : (
    <EnglishTerms {...contentProps} />
  );
}

function EnglishTerms({
  tenant,
  businessType,
  cancellationWindowHours,
  cancellationWindowHoursTrainer,
}: TermsContentProps) {
  const cancellationWindowTitle = formatCancellationWindowTitle(
    cancellationWindowHours,
  );
  const cancellationWindowText = formatCancellationWindowText(
    cancellationWindowHours,
  );
  const trainerSessionTerm = businessType === 'studio' ? 'trainer' : 'coach';
  const hasTrainerOverride =
    cancellationWindowHoursTrainer !== null &&
    cancellationWindowHoursTrainer !== cancellationWindowHours;

  return (
    <div className="prose prose-lg dark:prose-invert max-w-none">
      <h2 className="mt-8 mb-4 text-2xl font-bold">Introduction</h2>
      <p>
        Welcome to {tenant.name}. These Terms and Conditions (&quot;Terms&quot;)
        govern your use of our court booking and facility services.
      </p>
      <p>
        {tenant.name} uses the Ask AI platform for booking management. By using
        our services, you also agree to the{' '}
        <a href="/terms-of-service" className="text-primary underline">
          Ask AI Platform Terms of Service
        </a>
        .
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Booking Terms</h2>
      <p>When making a booking at {tenant.name}, you agree to:</p>
      <ul className="mb-4 list-disc pl-6">
        <li>Provide accurate contact information</li>
        <li>Arrive on time for your scheduled booking</li>
        <li>Follow all facility rules and guidelines</li>
        <li>Treat staff and other users with respect</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Cancellation Policy</h2>
      <p>
        The applicable cancellation policy is shown for your selected venue and
        booking type during checkout. The terms below describe the tenant-wide
        policy when a venue does not set its own cancellation window.
      </p>
      <ul className="mb-4 list-disc pl-6">
        {cancellationWindowHours === 0 ? (
          <li>
            <strong>Cancellation Notice:</strong> Bookings cancelled before the
            scheduled time are eligible for credit
          </li>
        ) : (
          <>
            <li>
              <strong>{cancellationWindowTitle} Notice Required:</strong>{' '}
              Bookings must be cancelled at least {cancellationWindowText}{' '}
              before the scheduled time
            </li>
            <li>
              <strong>Late Cancellations:</strong> Cancellations made less than{' '}
              {cancellationWindowText} before the scheduled time will not be
              eligible for credit
            </li>
          </>
        )}
        {hasTrainerOverride && (
          <li>
            <strong>{capitalize(trainerSessionTerm)} Session:</strong>{' '}
            {cancellationWindowHoursTrainer === 0
              ? `${capitalize(trainerSessionTerm)} sessions cancelled before the scheduled time are eligible for credit`
              : `${capitalize(trainerSessionTerm)} sessions require ${formatCancellationWindowText(cancellationWindowHoursTrainer ?? 0)} notice to be eligible for credit`}
          </li>
        )}
        <li>
          <strong>No-Shows:</strong> Failure to attend without cancellation will
          result in forfeiture of the booking amount
        </li>
      </ul>

      <h3 className="mt-6 mb-3 text-xl font-bold">How to Cancel</h3>
      <p>You can cancel your booking by:</p>
      <ul className="mb-4 list-disc pl-6">
        <li>Using the cancellation link in your booking confirmation</li>
        <li>Contacting us directly via phone or WhatsApp</li>
        <li>Through the booking platform</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Refund and Credit Policy</h2>
      <ul className="mb-4 list-disc pl-6">
        <li>
          <strong>No Cash Refunds:</strong> We do not provide cash refunds for
          cancelled bookings
        </li>
        <li>
          <strong>Credit Only:</strong> Eligible cancellations will receive
          credit for future bookings
        </li>
        <li>
          <strong>Credit Validity:</strong> Credit validity is stated when the
          credit is issued
        </li>
        <li>
          <strong>Non-Transferable:</strong> Credits cannot be transferred to
          another person
        </li>
      </ul>

      <h3 className="mt-6 mb-3 text-xl font-bold">Package Purchases</h3>
      <ul className="mb-4 list-disc pl-6">
        <li>
          Package credit validity depends on the package selected at purchase
        </li>
        <li>Unused package credits are non-refundable</li>
        <li>Packages cannot be transferred between users</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Payment</h2>
      <ul className="mb-4 list-disc pl-6">
        <li>Payments are processed securely through our platform</li>
        <li>
          We accept credit/debit cards, Apple Pay, and other supported methods
        </li>
        <li>All prices include applicable VAT</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Facility Rules</h2>
      <p>While using our facilities, you agree to:</p>
      <ul className="mb-4 list-disc pl-6">
        <li>Wear appropriate sports attire and footwear</li>
        <li>Use equipment responsibly and report any damage</li>
        <li>Follow safety guidelines and instructions from staff</li>
        <li>Vacate the court promptly at the end of your booking</li>
        <li>Not bring food or drinks onto the courts (unless permitted)</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Liability</h2>
      <p>
        {tenant.name} is not liable for any injuries, accidents, or loss of
        personal belongings while using our facilities. Users participate in
        activities at their own risk.
      </p>
      <p>
        We recommend that users have appropriate insurance coverage and consult
        a physician before engaging in physical activities.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Changes to Terms</h2>
      <p>
        We may update these Terms from time to time. Continued use of our
        services after changes constitutes acceptance of the new Terms.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Governing Law</h2>
      <p>
        These Terms are governed by the laws of the Kingdom of Saudi Arabia.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Contact Us</h2>
      <p>If you have any questions about these Terms, please contact us:</p>
      <p className="mb-1">
        <strong>{tenant.name}</strong>
      </p>
      <p className="mb-1">{tenant.address}</p>
      <p className="mb-1">
        {tenant.city}, {tenant.country}
      </p>
      <p className="mb-1">Email: {tenant.email}</p>
      <p className="mb-4">Phone: {tenant.phone}</p>
    </div>
  );
}

function ArabicTerms({
  tenant,
  cancellationWindowHours,
  cancellationWindowHoursTrainer,
}: TermsContentProps) {
  // "مدرب" (trainer/coach) is used for both studio and non-studio business
  // types in Arabic.
  const trainerSessionTermAr = 'مدرب';
  const hasTrainerOverride =
    cancellationWindowHoursTrainer !== null &&
    cancellationWindowHoursTrainer !== cancellationWindowHours;

  return (
    <div
      dir="rtl"
      className="prose prose-lg dark:prose-invert max-w-none text-right"
    >
      <h2 className="mt-8 mb-4 text-2xl font-bold">مقدمة</h2>
      <p>
        مرحبًا بك في {tenant.name}. تحكم هذه الشروط والأحكام
        (&quot;الشروط&quot;) استخدامك لخدمات حجز الملاعب والمرافق لدينا.
      </p>
      <p>
        يستخدم {tenant.name} منصة Ask AI لإدارة الحجوزات. وباستخدامك لخدماتنا،
        فإنك توافق أيضًا على{' '}
        <a href="/terms-of-service" className="text-primary underline">
          شروط خدمة منصة Ask AI
        </a>
        .
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">شروط الحجز</h2>
      <p>عند إجراء حجز لدى {tenant.name}، فإنك توافق على:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>تقديم معلومات اتصال دقيقة</li>
        <li>الحضور في الوقت المحدد للحجز</li>
        <li>الالتزام بجميع قواعد المرفق وإرشاداته</li>
        <li>معاملة الموظفين والمستخدمين الآخرين باحترام</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">سياسة الإلغاء</h2>
      <p>
        تظهر سياسة الإلغاء المطبقة لموقعك ونوع حجزك أثناء إتمام الحجز. توضح
        الشروط أدناه سياسة المنشأة العامة عندما لا يحدد الموقع مهلة إلغاء خاصة
        به.
      </p>
      <ul className="mb-4 list-disc pr-6">
        {cancellationWindowHours === 0 ? (
          <li>
            <strong>إشعار الإلغاء:</strong> الحجوزات التي يتم إلغاؤها قبل الوقت
            المحدد تكون مؤهلة للحصول على رصيد
          </li>
        ) : (
          <>
            <li>
              <strong>
                يلزم الإشعار قبل{' '}
                {formatCancellationWindowTextAr(cancellationWindowHours)}:
              </strong>{' '}
              يجب إلغاء الحجوزات قبل الوقت المحدد بمدة لا تقل عن{' '}
              {formatCancellationWindowTextAr(cancellationWindowHours)}
            </li>
            <li>
              <strong>الإلغاء المتأخر:</strong> عمليات الإلغاء التي تتم قبل
              الوقت المحدد بأقل من{' '}
              {formatCancellationWindowTextAr(cancellationWindowHours)} لن تكون
              مؤهلة للحصول على رصيد
            </li>
          </>
        )}
        {hasTrainerOverride && (
          <li>
            <strong>جلسة {trainerSessionTermAr}:</strong>{' '}
            {cancellationWindowHoursTrainer === 0
              ? `جلسات ${trainerSessionTermAr} التي يتم إلغاؤها قبل الوقت المحدد تكون مؤهلة للحصول على رصيد`
              : `تتطلب جلسات ${trainerSessionTermAr} إشعارًا قبل ${formatCancellationWindowTextAr(cancellationWindowHoursTrainer ?? 0)} لتكون مؤهلة للحصول على رصيد`}
          </li>
        )}
        <li>
          <strong>عدم الحضور:</strong> عدم الحضور دون إلغاء يؤدي إلى فقدان مبلغ
          الحجز
        </li>
      </ul>

      <h3 className="mt-6 mb-3 text-xl font-bold">كيفية الإلغاء</h3>
      <p>يمكنك إلغاء حجزك عن طريق:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>استخدام رابط الإلغاء الموجود في تأكيد حجزك</li>
        <li>التواصل معنا مباشرةً عبر الهاتف أو واتساب</li>
        <li>من خلال منصة الحجز</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">سياسة الاسترداد والرصيد</h2>
      <ul className="mb-4 list-disc pr-6">
        <li>
          <strong>لا توجد استردادات نقدية:</strong> لا نقدم استردادات نقدية
          للحجوزات الملغاة
        </li>
        <li>
          <strong>رصيد فقط:</strong> عمليات الإلغاء المؤهلة ستحصل على رصيد
          للحجوزات المستقبلية
        </li>
        <li>
          <strong>صلاحية الرصيد:</strong> يتم تحديد صلاحية الرصيد عند إصداره
        </li>
        <li>
          <strong>غير قابل للتحويل:</strong> لا يمكن تحويل الرصيد إلى شخص آخر
        </li>
      </ul>

      <h3 className="mt-6 mb-3 text-xl font-bold">شراء الباقات</h3>
      <ul className="mb-4 list-disc pr-6">
        <li>تعتمد صلاحية رصيد الباقة على الباقة المختارة عند الشراء</li>
        <li>أرصدة الباقات غير المستخدمة غير قابلة للاسترداد</li>
        <li>لا يمكن تحويل الباقات بين المستخدمين</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">الدفع</h2>
      <ul className="mb-4 list-disc pr-6">
        <li>تتم معالجة المدفوعات بشكل آمن من خلال منصتنا</li>
        <li>
          نقبل بطاقات الائتمان/الخصم، وApple Pay، وغيرها من وسائل الدفع المدعومة
        </li>
        <li>جميع الأسعار تشمل ضريبة القيمة المضافة المطبقة</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">قواعد المرفق</h2>
      <p>أثناء استخدامك لمرافقنا، فإنك توافق على:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>ارتداء ملابس وأحذية رياضية مناسبة</li>
        <li>استخدام المعدات بمسؤولية والإبلاغ عن أي ضرر</li>
        <li>اتباع إرشادات السلامة وتعليمات الموظفين</li>
        <li>إخلاء الملعب فور انتهاء وقت حجزك</li>
        <li>عدم إحضار الطعام أو الشراب إلى الملاعب (ما لم يُسمح بذلك)</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">المسؤولية</h2>
      <p>
        لا يتحمل {tenant.name} أي مسؤولية عن أي إصابات أو حوادث أو فقدان
        للممتلكات الشخصية أثناء استخدام مرافقنا. يشارك المستخدمون في الأنشطة على
        مسؤوليتهم الخاصة.
      </p>
      <p>
        نوصي بأن يكون لدى المستخدمين تغطية تأمينية مناسبة وأن يستشيروا طبيبًا
        قبل ممارسة الأنشطة البدنية.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">التعديلات على الشروط</h2>
      <p>
        قد نقوم بتحديث هذه الشروط من وقت لآخر. ويُعد استمرارك في استخدام خدماتنا
        بعد التعديلات قبولًا للشروط الجديدة.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">القانون الحاكم</h2>
      <p>تخضع هذه الشروط لقوانين المملكة العربية السعودية.</p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">تواصل معنا</h2>
      <p>إذا كان لديك أي أسئلة حول هذه الشروط، يرجى التواصل معنا:</p>
      <p className="mb-1">
        <strong>{tenant.name}</strong>
      </p>
      <p className="mb-1">{tenant.address}</p>
      <p className="mb-1">
        {tenant.city}، {tenant.country}
      </p>
      <p className="mb-1">البريد الإلكتروني: {tenant.email}</p>
      <p className="mb-4">الهاتف: {tenant.phone}</p>
    </div>
  );
}

function formatCancellationWindowTitle(hours: number) {
  if (hours === 1) {
    return '1-Hour';
  }

  return `${hours}-Hour`;
}

function formatCancellationWindowText(hours: number) {
  if (hours === 1) {
    return '1 hour';
  }

  return `${hours} hours`;
}

function formatCancellationWindowTextAr(hours: number) {
  if (hours === 1) {
    return 'ساعة واحدة';
  }

  if (hours === 2) {
    return 'ساعتين';
  }

  if (hours >= 3 && hours <= 10) {
    return `${hours} ساعات`;
  }

  return `${hours} ساعة`;
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
